import { BookingViewOpened, ClearBrokerDetails, FetchRequest, MarkAsRead } from 'acscp/actions';
import { withPageLayout } from 'acscp/components/layout/pageLayout';
import { PresentationComponentBase } from 'acscp/components/presentationComponentBase';
import { AppState, Booking } from 'acscp/store';
import { Route, RouteComponentProps } from 'react-router-dom';
import { Dispatch } from 'redux';
import { BrokerDetailsForBookingRequest } from 'acscp/api/requests';
import React from 'react';
import { BookingRequest } from 'acscp/api/requests/bookingRequest';
import { Quotes } from 'acscp/components/quotes/quotes';
import { Itinerary } from 'acscp/components/itinerary/itinerary';
import { AwaitingItinerary } from 'acscp/components/booking/awaitingItinerary';
import { ExpiredBooking, FlownBooking } from 'acscp/components/booking/unavailableBooking';
import { LoadingIndicator } from 'acscp/components/common/loadingIndicator';
import { connectWithRequests } from 'acscp/store/helpers/connectWithRequests';
import { userIsLoggedIn } from 'acscp/store/helpers/authHelper';
import { NotFound } from 'acscp/web/components/pages/errorPage';
import { SharingModal } from '../sharing/sharingModal';
import { ModifyModal } from '../booking/modifyModal';

interface TripPageStateProps {
    bookingDetails: Booking | undefined;
    bookingContentIdentifier: string | undefined;
    isLoggedIn: boolean;
}

interface TripPageDispatchProps {
    bookingViewOpened: (booking: Booking) => void;
    fetchBookingDetails: () => void;
    fetchBrokerDetails: () => void;
    markBookingAsViewed: (ownedByCurrentUser: boolean, contentIdentifier: string) => void;
    clearBrokerDetails: () => void;
}

export interface TripPageParams {
    bookingId: string;
}

type TripPageOwnProps = RouteComponentProps<TripPageParams>;

function getSubcomponentForBooking(booking: Booking): JSX.Element {
    switch (booking.status) {
        case 'CheckingAvailability':
        case 'FlightPlanning':
            return <AwaitingItinerary bookingDetails={booking} />;
        case 'ItineraryAvailable':
            return <Itinerary bookingDetails={booking} />;
        case 'Flown':
            return <FlownBooking />;
        case 'Expired':
            return <ExpiredBooking />;
        case 'AwaitingQuotes':
        case 'QuotesAvailable':
            return <Quotes bookingDetails={booking} />;
        case 'Enquiry':
            return <NotFound />;
        default:
            throw new Error(`Unexpected booking status ${booking.status}`);
    }
}

export class TripPagePresentational
        extends PresentationComponentBase<TripPageOwnProps, TripPageStateProps, TripPageDispatchProps, {}> {

    componentDidMount(): void {
        this.props.fetchBookingDetails();

        this.onBookingChanged();
    }

    componentDidUpdate(prevProps: TripPageStateProps): void {
        if (prevProps.bookingDetails !== this.props.bookingDetails) {
            this.onBookingChanged();
        }
    }

    private onBookingChanged(): void {
        if (this.props.bookingDetails) {
            this.props.bookingViewOpened(this.props.bookingDetails);
            if (this.props.bookingContentIdentifier !== undefined) {
                this.props.markBookingAsViewed(this.props.isLoggedIn, this.props.bookingContentIdentifier);
            }
            if (!this.props.isLoggedIn) {
                this.props.fetchBrokerDetails();
            }
        }
    }

    componentWillUnmount(): void {
        if (!this.props.isLoggedIn) {
            this.props.clearBrokerDetails();
        }
    }

    render(): JSX.Element {
        const booking = this.props.bookingDetails;
        if (booking) {
            if (booking.autoPublishedToCustomerPortal) {
                return <NotFound/>;
            } else {
                return (
                    <div>
                        {getSubcomponentForBooking(booking)}
                        <Route path="/trip/:bookingId/share" exact={true} render={() => <SharingModal booking={booking}/>}/>
                        <Route path="/trip/:bookingId/modify" exact={true} render={() => <ModifyModal booking={booking}/>}/>
                    </div>
                );
            }
        } else {
            return <LoadingIndicator />;
        }
    }
}

function mapStateToProps(state: AppState, ownProps: TripPageOwnProps): TripPageStateProps {
    const bookingId = ownProps.match.params.bookingId;
    const bookingsReadDetails = state.bookingsReadDetails[bookingId];
    return {
        bookingDetails: state.bookingsDetails[bookingId],
        bookingContentIdentifier: bookingsReadDetails ? bookingsReadDetails.contentIdentifier : undefined,
        isLoggedIn: userIsLoggedIn(state.auth)
    };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: TripPageOwnProps): TripPageDispatchProps {
    const bookingId = ownProps.match.params.bookingId;
    return {
        bookingViewOpened: (booking: Booking) => dispatch(new BookingViewOpened(booking)),
        fetchBookingDetails: () => dispatch(new FetchRequest(new BookingRequest(bookingId))),
        fetchBrokerDetails: () =>  dispatch(new FetchRequest(new BrokerDetailsForBookingRequest(bookingId))),
        markBookingAsViewed: (isUserLoggedIn, contentIdentifier) =>
            dispatch(new MarkAsRead(bookingId, isUserLoggedIn, contentIdentifier)),
        clearBrokerDetails: () => dispatch(new ClearBrokerDetails()),
    };
}

const Trip = withPageLayout(
    connectWithRequests(mapStateToProps, mapDispatchToProps)(
        TripPagePresentational
    )
);

export { Trip };
